// Austin neighborhood / ZIP-level landing content.
// Each entry powers a dedicated /locations/austin/neighborhoods/$slug page
// with its own hero, deep-dive, industry focus, top services, FAQs, and
// Organization + LocalBusiness JSON-LD.

export type AustinNeighborhoodFAQ = { q: string; a: string };

export type AustinNeighborhood = {
  slug: string;
  name: string;           // display name, e.g. "South Austin"
  displaySubtitle: string; // small qualifier under the H1
  zips: string[];         // list of covered ZIP codes
  areaServed: string[];   // named sub-areas / adjacent cities used for JSON-LD areaServed
  latLng?: { lat: number; lng: number };
  hero: {
    eyebrow: string;
    headline: string;
    deck: string;
  };
  tldr: string[];
  searchLandscape: string[]; // 3-5 paragraphs on how search actually behaves here
  industryFocus: { industry: string; slug?: string; note: string }[];
  topServices: string[]; // slugs of SERVICES most relevant here
  topZips: { zip: string; note: string }[];
  playbook: string[]; // tactical bullets specific to this neighborhood
  faqs: AustinNeighborhoodFAQ[];
};

export const AUSTIN_NEIGHBORHOODS: AustinNeighborhood[] = [
  {
    slug: "south-austin",
    name: "South Austin",
    displaySubtitle: "78704 · 78745 · 78748",
    zips: ["78704", "78745", "78748"],
    areaServed: [
      "Bouldin Creek",
      "Zilker",
      "South Congress",
      "Travis Heights",
      "South Lamar",
      "Sunset Valley",
      "Manchaca",
    ],
    latLng: { lat: 30.2380, lng: -97.7686 },
    hero: {
      eyebrow: "Austin neighbourhood · SEO & AI search",
      headline: "SEO for South Austin businesses in 78704, 78745, and 78748.",
      deck:
        "The highest home-services demand density in the metro, the most contested restaurant Local Pack in Texas, and a review economy that punishes lazy GBPs within a quarter. Here is what actually earns rankings in South Austin.",
    },
    tldr: [
      "78704 has the highest GBP competition of any Austin ZIP — reviews, categories, and hours must be maintained weekly, not quarterly.",
      "78745 quietly runs larger service tickets than 78704 and is where most home-service operators earn their best margins.",
      "78748 is still a growth ZIP for reviews — disciplined operators can leapfrog older competitors inside 6 months.",
      "AI Overview citations for 'best [service] in South Austin' are winnable when service pages include ZIP-specific proof and named local landmarks.",
    ],
    searchLandscape: [
      "South Austin is the single most contested residential catchment in the Austin metro for home services, food & beverage, wellness, and personal-services categories. 78704 alone hosts more than 400 businesses actively competing in the 3-pack for the top 20 service-area categories, and the top of the map is refreshed constantly by Google's proximity + prominence logic.",
      "The pattern we see across engagements: operators who treat 78704, 78745, and 78748 as one 'South Austin' catchment lose to operators who split them into three distinct service-area pages, each with ZIP-specific proof, imagery, and reviews. Google reads the difference because the entity signals differ.",
      "The review economy in South Austin is unusually fast. New reviews attributed to a specific 78704 landmark (a restaurant, a boutique, a well-known church) noticeably move Local Pack position inside 2–3 weeks. Operators who treat review generation as a checkbox lose visibility fastest here.",
      "AI Overviews on South Austin queries are already active for restaurants, HVAC, plumbing, and pest control, and the citation set typically includes 3 sources: one dominant aggregator (Yelp or Angi), one operator with proper LocalBusiness + FAQ schema, and one editorial source. The operator slot is winnable, and we've earned it for clients in each of these ZIPs.",
    ],
    industryFocus: [
      {
        industry: "Home services (HVAC, plumbing, electrical, pest control)",
        slug: "hvac-home-services",
        note:
          "Highest inbound-call value in the metro during the April–September summer curve. Per-ZIP service-area pages plus a summer-ready content wave (shipped by early April) is the pattern that wins here.",
      },
      {
        industry: "Restaurants, bars & F&B",
        slug: "restaurants-food",
        note:
          "South Congress and South Lamar dominate discovery for out-of-town search. GBP hygiene (menu, hours, categories, photos) matters more than any single content asset — but a schema-rich menu page still moves AI Overview citation share.",
      },
      {
        industry: "Automotive repair & specialists",
        slug: "automotive",
        note:
          "78745 has one of the densest concentrations of independent and European specialist shops in Texas. Per-repair-category pages plus review velocity engineered around drop-off / pickup moments is the highest-yield combination.",
      },
      {
        industry: "Wellness, med-spa & personal services",
        slug: "healthcare-wellness",
        note:
          "78704 buyers research heavily before booking. FAQ / HowTo schema on procedure pages plus authentic before/after content is what earns the operator slot in AI Overviews here.",
      },
    ],
    topServices: [
      "local-seo",
      "google-business-profile",
      "aeo-geo",
      "reputation-management",
      "technical-seo",
      "content-strategy",
    ],
    topZips: [
      { zip: "78704", note: "SoCo, Bouldin, Zilker, Travis Heights — the highest-competition ZIP in Austin for consumer services." },
      { zip: "78745", note: "South Lamar, Sunset Valley, Manchaca — larger residential tickets, better home-service margins." },
      { zip: "78748", note: "Southpark Meadows and southern extension — a growth ZIP where new reviews compound fastest." },
    ],
    playbook: [
      "Ship a service-area page per ZIP (78704, 78745, 78748) with ZIP-specific proof, imagery, and reviews — never one blended 'South Austin' page.",
      "Set up a weekly review cadence tied to real invoice events (drop-off, service completion, follow-up call) — target 4–8 net-new reviews per month per location.",
      "Add LocalBusiness + FAQPage + (where relevant) HowTo schema to every service page; South Austin's AI Overview citation set rewards it.",
      "Publish a summer-ready home-services content wave by April; a fall / heating wave by October. Both windows are when demand curves inflect.",
      "Refresh GBP photos monthly with genuine on-site imagery — Google's photo-recency signal is unusually strong in 78704.",
    ],
    faqs: [
      {
        q: "Which Austin ZIP codes count as South Austin for local SEO?",
        a: "For our engagements, 'South Austin' means 78704, 78745, and 78748. 78741 (Riverside) and 78744 (Montopolis) are frequently grouped in by aggregators but behave more like Southeast Austin — different competitor sets, different buyer language, different Local Pack composition.",
      },
      {
        q: "Do I need a separate page for each South Austin ZIP?",
        a: "For any operator whose average ticket is above roughly $250, yes. Google reads the entity difference between 78704, 78745, and 78748 (different reviews, different landmarks, different competitor overlap), and per-ZIP service-area pages compound that difference. For lower-ticket operators, one solid South Austin hub page plus GBP hygiene is enough.",
      },
      {
        q: "How competitive is the map pack in 78704?",
        a: "For home services, restaurants, and personal services, 78704 is among the top ten most competitive ZIP-level Local Packs in Texas. Winning here requires review velocity above roughly 6 net-new per month, GBP category and hours accuracy checked weekly, and LocalBusiness schema on every service and location page.",
      },
      {
        q: "Do you serve neighbourhoods outside 78704/78745/78748?",
        a: "Yes. Optimize Plus serves every Austin neighborhood plus the surrounding Central Texas metros — see the Austin location page for the full areaServed list. Our office and mailing address is in 78745 (South Austin).",
      },
    ],
  },
  {
    slug: "north-austin-domain",
    name: "The Domain / North Austin",
    displaySubtitle: "78758 · 78759",
    zips: ["78758", "78759"],
    areaServed: [
      "The Domain",
      "Rundberg",
      "Wells Branch",
      "Gracywoods",
      "North Burnet",
      "Braker",
    ],
    latLng: { lat: 30.4013, lng: -97.7250 },
    hero: {
      eyebrow: "Austin neighbourhood · B2B & AI search",
      headline: "SEO for North Austin businesses in 78758 and 78759.",
      deck:
        "Home to Meta, Amazon, Apple's Austin campus, and one of the densest professional-services ecosystems in Texas. Weekday intent and weekend intent are two different markets — winning content splits them cleanly.",
    },
    tldr: [
      "78758 and 78759 host the largest concentration of professional B2B and tech-adjacent buyers in Austin.",
      "Weekday intent is dominated by 'near office' lunch, coffee, and errands; weekend intent is residential.",
      "B2B SaaS and professional services here rank on entity-graph strength more than on Local Pack presence — different playbook.",
      "AI Overview citation share on 'best [B2B service] in Austin' is the highest-leverage growth channel for this catchment in 2026.",
    ],
    searchLandscape: [
      "The Domain / North Austin catchment is Austin's second downtown. During weekday business hours, the search intent inside 78758 and 78759 looks nothing like the rest of Austin — a huge share of queries are proximity-tight, high-intent, and buyer-immediate (lunch, coffee, meetings, printing, tailoring, dry cleaning, urgent care).",
      "In the evenings and on weekends the same ZIPs revert to residential intent — HVAC, plumbing, home services, restaurants, and family-oriented queries. Operators who write one blended 'North Austin' page lose to operators who ship separate weekday and evening content strategies.",
      "For B2B SaaS, IT services, staffing, and professional services, this catchment is where most Austin-headquartered mid-market buyers actually work. Ranking in the Local Pack is less critical than being cited by AI Overviews on 'best [service] for Austin businesses' — that citation set is what fills sales pipelines.",
      "Entity graph strength (structured data, LinkedIn + Crunchbase + G2 + Clutch consistency, Wikidata presence for larger vendors) is a heavier ranking factor for the B2B queries dominant here than for any other Austin catchment.",
    ],
    industryFocus: [
      {
        industry: "B2B SaaS & tech",
        slug: "saas",
        note:
          "Entity-graph consistency (LinkedIn, Crunchbase, G2, Wikidata) plus schema-rich comparison pages plus AI Overview citation capture. Local Pack is secondary here.",
      },
      {
        industry: "Professional services (legal, financial, consulting)",
        slug: "professional-services",
        note:
          "Author-attributed thought leadership + FAQ schema + reviews on Google, Clutch, and Avvo (for legal) is what earns AI Overview citations for high-value B2B queries.",
      },
      {
        industry: "Restaurants, cafes & lunch spots",
        slug: "restaurants-food",
        note:
          "Weekday lunch is a completely different query set than weekend brunch. Split menu / hours / offer pages by daypart. GBP photo cadence matters intensely here.",
      },
      {
        industry: "Home services (evening / weekend intent)",
        slug: "hvac-home-services",
        note:
          "Residential demand from 78727, 78729, 78750 spills into 78758/78759 evening queries. Per-ZIP service-area pages plus after-hours GBP messaging captures it.",
      },
    ],
    topServices: [
      "aeo-geo",
      "saas-seo",
      "content-strategy",
      "digital-pr",
      "technical-seo",
      "analytics-reporting",
    ],
    topZips: [
      { zip: "78758", note: "Rundberg, Gracywoods, Wells Branch — mixed residential + retail with heavy commuter traffic." },
      { zip: "78759", note: "The Domain, North Burnet, Braker — B2B, tech HQs, and the densest professional-services corridor in Austin." },
    ],
    playbook: [
      "Split content by daypart — weekday-office intent vs evening-residential intent — with separate URLs, headings, and internal linking clusters.",
      "For B2B: prioritise author-attributed thought leadership, entity-graph work (Wikidata, LinkedIn, Crunchbase consistency), and AI Overview citation capture over Local Pack chasing.",
      "For F&B / retail: refresh GBP photos weekly, keep hours accurate to the minute (Google penalises misaligned hours in this catchment hard), and publish a lunch-focused menu page with schema.",
      "Track AI Overview citation share monthly on 20–40 tracked queries — for this catchment, that KPI leads Local Pack position by 2–3 quarters.",
      "Instrument analytics with UTM discipline — sales cycles here are longer and first-touch attribution matters more than in South Austin.",
    ],
    faqs: [
      {
        q: "Is The Domain the same market as North Austin for SEO?",
        a: "No. The Domain (mostly 78759) is a distinct B2B / retail sub-market with weekday-heavy intent and premium ticket sizes. 'North Austin' more broadly (78758, plus overlap into 78727/78729) is a residential + mixed-use catchment. We ship separate service-area pages for each when a client wants both.",
      },
      {
        q: "What's the biggest local SEO mistake operators make in North Austin?",
        a: "Writing one 'North Austin / Domain' page that tries to serve weekday commuter intent and weekend residential intent with the same copy. Both intents lose. The fix is two URLs, two headings, two internal linking clusters — and letting Google infer the difference from the entity signals we send.",
      },
      {
        q: "Do B2B SaaS companies in The Domain need local SEO?",
        a: "Local SEO in the classic Local Pack sense — usually not the priority. What actually moves pipeline is entity-graph strength, comparison-page rankings, review consistency across G2/Clutch/Google, and AI Overview citation share on 'best [tool] for [use case]' queries. See our AEO/GEO service page for how we run that programme.",
      },
      {
        q: "How does Optimize Plus cover North Austin from its 78745 office?",
        a: "Optimize Plus operates as a full-service Austin agency — our office is in 78745 (South Austin) but our engagements span every Austin catchment. Client meetings happen on-site, at The Domain, or remotely depending on preference.",
      },
    ],
  },
  {
    slug: "east-austin",
    name: "East Austin",
    displaySubtitle: "78702 · 78721 · 78723",
    zips: ["78702", "78721", "78723"],
    areaServed: [
      "Holly",
      "Chestnut",
      "Rosewood",
      "Govalle",
      "Mueller",
      "Windsor Park",
      "MLK",
    ],
    latLng: { lat: 30.2758, lng: -97.7106 },
    hero: {
      eyebrow: "Austin neighbourhood · Local SEO",
      headline: "SEO for East Austin businesses in 78702, 78721, and 78723.",
      deck:
        "The fastest-changing catchment in the metro. Every buyer persona has reset in under a decade, and service-area pages written for East Austin in 2018 read tone-deaf today. Here is what we refresh on a 12-month cycle to keep operators ranking.",
    },
    tldr: [
      "East Austin's buyer personas have shifted more than any other Austin catchment over the last decade.",
      "Service pages older than 18 months typically underperform their potential by 30-60% on Local Pack and organic here.",
      "Reviews from named East Austin landmarks (Holly, Mueller, Windsor Park) still carry disproportionate weight in the Pack.",
      "AI Overview coverage on 'best [service] in East Austin' is one of the most winnable slots in Austin — the citation set is sparse and refreshes fast.",
    ],
    searchLandscape: [
      "East Austin is the most demographically and commercially fluid catchment in the metro. Within the last decade, 78702 has moved from primarily residential and independently-owned to one of the most mixed retail / hospitality / creative-services corridors in Texas. Service-area pages that don't reflect the current buyer language of the catchment silently underperform.",
      "78721 and 78723 (Mueller, Windsor Park) have added significant middle-income residential volume, changing the Local Pack composition for home services, dental, veterinary, and family-oriented categories. Operators who refreshed their service-area content in 2023 and haven't touched it since are usually overdue.",
      "Review signals from named East Austin landmarks are still disproportionately weighted. A review that references 'Holly', 'Mueller', 'MLK', or a specific East Austin street tends to move Local Pack position more than a generic Austin review — Google is reading the location entity strength.",
      "AI Overview coverage on 'best [service] in East Austin' currently pulls a citation set of only 2–3 sources for most categories, and it refreshes faster than any other Austin catchment. Operators who ship schema-rich, ZIP-specific service pages capture the operator slot inside 60–90 days.",
    ],
    industryFocus: [
      {
        industry: "Restaurants, bars & creative F&B",
        slug: "restaurants-food",
        note:
          "78702 has one of the densest F&B corridors in Austin. GBP photo + menu freshness, plus a schema-rich menu page, is table stakes. Newsletter and email capture from local search is the leverage nobody uses.",
      },
      {
        industry: "Home services (residential expansion)",
        slug: "hvac-home-services",
        note:
          "Mueller, Windsor Park, and Chestnut have added significant residential volume. Per-ZIP service-area pages plus review generation from named streets is what moves the Pack here.",
      },
      {
        industry: "Creative & professional services (design, agencies, studios)",
        slug: "professional-services",
        note:
          "Author-attributed content plus portfolio schema plus community sponsorships (East Austin Studio Tour, EAST) is the local-authority pattern that compounds fastest.",
      },
      {
        industry: "Dental, veterinary & family services",
        slug: "healthcare-wellness",
        note:
          "The residential shift into Mueller and Windsor Park has created durable new demand for family-oriented services. Google Business Profile hygiene plus review velocity above 4/month wins here.",
      },
    ],
    topServices: [
      "local-seo",
      "google-business-profile",
      "content-strategy",
      "aeo-geo",
      "digital-pr",
      "reputation-management",
    ],
    topZips: [
      { zip: "78702", note: "Holly, Chestnut, MLK — dense F&B, creative services, and rapidly-changing retail." },
      { zip: "78721", note: "MLK East, Govalle — residential + light industrial, big shift toward family services." },
      { zip: "78723", note: "Mueller, Windsor Park — highest residential growth in East Austin; family-service demand is compounding." },
    ],
    playbook: [
      "Refresh every East Austin service-area page on a rolling 12-month cycle — buyer language, imagery, and named-landmark references all age fast here.",
      "Ask for reviews that mention a named East Austin street, park, or landmark — those reviews move the Local Pack disproportionately.",
      "Publish a per-ZIP service-area page for 78702, 78721, and 78723 with distinct proof and imagery — do not blend.",
      "Sponsor or participate in East Austin cultural events (EAST, Mueller Farmers Market, HOPE Farmers Market) and earn contextual backlinks from event pages.",
      "Track AI Overview citation share for East Austin queries monthly — refresh happens fast, and the operator slot is available in most categories.",
    ],
    faqs: [
      {
        q: "How much has SEO for East Austin changed in the last five years?",
        a: "More than any other Austin catchment. Buyer personas, review language, and even the categories being searched have shifted substantially. Service-area pages written before 2022 for East Austin should be audited before another peak season — the ones we've refreshed have typically recovered 30–60% of lost potential inside two quarters.",
      },
      {
        q: "Which East Austin ZIP has the highest home-services growth?",
        a: "78723 (Mueller, Windsor Park) has been the biggest residential expansion in East Austin over the last five years, followed by 78721. That has translated into durable, compounding demand for HVAC, plumbing, dental, veterinary, and family-oriented services.",
      },
      {
        q: "Do East Austin restaurants really need SEO if they already have Instagram traction?",
        a: "Yes — Instagram is discovery; Google is intent. The queries 'best brunch East Austin', 'happy hour near me', and 'brunch 78702' fire millions of times a year, and the AI Overview citation set for those queries is usually 2–3 sources. A schema-rich, current menu page plus disciplined GBP work captures that intent traffic, which converts far more predictably than social discovery.",
      },
      {
        q: "How often should East Austin content be refreshed?",
        a: "For service pages, every 12 months at minimum. For neighbourhood copy specifically (buyer language, imagery, named landmarks), every 12 months without exception. East Austin ages content faster than any other Austin catchment we cover.",
      },
    ],
  },
  {
    slug: "west-austin-westlake",
    name: "West Austin / Westlake / Lakeway",
    displaySubtitle: "78746 · 78738 · 78734",
    zips: ["78746", "78738", "78734"],
    areaServed: [
      "Westlake",
      "Rollingwood",
      "Bee Cave",
      "Lakeway",
      "Steiner Ranch",
      "Barton Creek",
      "Rob Roy",
    ],
    latLng: { lat: 30.2952, lng: -97.8103 },
    hero: {
      eyebrow: "Austin neighbourhood · Premium local SEO",
      headline: "SEO for Westlake, Lakeway, and Bee Cave businesses in 78746, 78738, and 78734.",
      deck:
        "The highest average ticket in the Austin metro, and the slowest review velocity. Winning here is less about volume and more about depth — case studies, schema, and social proof from named local clients.",
    },
    tldr: [
      "Highest average customer ticket of any Austin catchment across nearly every service category.",
      "Buyers research longer, book less impulsively, and leave reviews slower than in any other Austin ZIP set.",
      "Depth beats volume — deep case studies, robust FAQ/HowTo schema, and named local social proof win Packs here.",
      "GBP alone is rarely enough; the winning stack is GBP + schema-rich service pages + branded on-page proof.",
    ],
    searchLandscape: [
      "The Westlake / Lakeway / Bee Cave catchment is Austin's premium-ticket market. Every category — from HVAC to dental to legal to landscaping to fitness — runs meaningfully higher average tickets than the same category in South, East, or North Austin. That changes what buyers do before they book.",
      "Buyers here research substantially longer than the Austin average. They read case studies, they read About and Team pages, they check reviews across multiple sources, and they often check LinkedIn or a professional directory before booking. Service pages need to earn that scrutiny — thin pages lose immediately.",
      "Review velocity in 78746, 78738, and 78734 is notably slower than in 78704 or 78702. Operators who chase raw review volume the way they would in South Austin usually underperform; operators who focus on high-quality, detailed reviews from recognisable local clients outperform.",
      "AI Overview coverage on 'best [service] in Westlake' or 'best [service] in Lakeway' is winnable, and the citation set is more forgiving here — depth of content, FAQPage schema, and named-client proof are what tend to earn the operator slot.",
    ],
    industryFocus: [
      {
        industry: "Legal, financial & professional services",
        slug: "professional-services",
        note:
          "Author-attributed thought leadership, schema-rich attorney/advisor bio pages, and Avvo / Clutch consistency drive results. Reviews here matter for trust more than for rank position.",
      },
      {
        industry: "Home services, landscaping & luxury home",
        slug: "hvac-home-services",
        note:
          "Deep project case studies with real client photography, plus per-service pages, plus GBP with premium imagery, is the pattern. Depth wins over frequency.",
      },
      {
        industry: "Medical, dental & med-spa",
        slug: "healthcare-wellness",
        note:
          "FAQ and HowTo schema on procedure pages plus authentic case documentation plus doctor-attributed content — the combination that earns AI Overview citations for high-consideration medical queries.",
      },
      {
        industry: "Automotive (European specialists)",
        slug: "automotive",
        note:
          "78738 and 78746 concentrate luxury and European vehicle ownership. Per-marque, per-repair pages plus named-vehicle case studies is the winning content shape.",
      },
    ],
    topServices: [
      "local-seo",
      "content-strategy",
      "aeo-geo",
      "digital-pr",
      "web-design",
      "reputation-management",
    ],
    topZips: [
      { zip: "78746", note: "Westlake, Rollingwood, Barton Creek — Austin's densest premium-ticket residential market." },
      { zip: "78738", note: "Bee Cave, Spanish Oaks — retail + premium residential; Hill Country Galleria dominates local retail search." },
      { zip: "78734", note: "Lakeway, The Hills — waterfront + retirement + premium residential; slower to review, longer to decide." },
    ],
    playbook: [
      "Invest in depth — every service page should include at least one detailed, named case study with real client imagery.",
      "Add FAQ and HowTo schema to every service page; premium buyers in this catchment read the FAQs before they call.",
      "Ask for detailed, named reviews from recognisable local clients. Two paragraph-long reviews often outperform ten short ones for Pack position here.",
      "Publish author-attributed content from owners, doctors, attorneys, or engineers — buyers here check credentials before booking.",
      "Track AI Overview citation share monthly on 'best [service] in Westlake / Lakeway / Bee Cave' — the operator slot is available in most categories with disciplined content.",
    ],
    faqs: [
      {
        q: "Is Westlake really a different SEO market than the rest of Austin?",
        a: "Yes. Average ticket, buyer research behaviour, review velocity, and content-consumption patterns are all meaningfully different in 78746, 78738, and 78734 than in 78704 or 78702. Applying a South Austin playbook here almost always underperforms, and vice versa.",
      },
      {
        q: "How many reviews per month should a Westlake service business target?",
        a: "Roughly 2–4 detailed, named reviews per month is the range we recommend for most premium-ticket categories in Westlake and Lakeway. Chasing raw volume the way you would in 78704 tends to hurt more than help — buyers here read reviews carefully, and a page of short, generic reviews reads as manufactured.",
      },
      {
        q: "Are Bee Cave and Lakeway the same market for local SEO?",
        a: "Related but not identical. Bee Cave (78738) has a stronger retail centre of gravity thanks to the Hill Country Galleria; Lakeway (78734) is more residential and waterfront-oriented. We ship distinct service-area pages for each when a client operates in both, because the query language and competitor set differ.",
      },
      {
        q: "Does Optimize Plus work with premium home-service brands in this catchment?",
        a: "Yes. We run engagements with legal, financial, medical, home-service, and automotive operators across 78746, 78738, and 78734. See the Austin location page and our portfolio for representative work.",
      },
    ],
  },
  {
    slug: "northwest-austin",
    name: "Northwest Austin / Cedar Park adjacent",
    displaySubtitle: "78727 · 78729 · 78750",
    zips: ["78727", "78729", "78750"],
    areaServed: [
      "Anderson Mill",
      "Jester",
      "Jollyville",
      "Balcones Woods",
      "Milwood",
      "Great Hills",
      "Spicewood Springs",
    ],
    latLng: { lat: 30.4494, lng: -97.7936 },
    hero: {
      eyebrow: "Austin neighbourhood · Multi-metro local SEO",
      headline: "SEO for Northwest Austin businesses in 78727, 78729, and 78750.",
      deck:
        "The overlap zone where Austin, Cedar Park, and Round Rock Local Packs collide. The single most common local SEO mistake in Austin lives here — and it's fixable with per-city service-area treatment.",
    },
    tldr: [
      "78727, 78729, and 78750 straddle the Austin / Cedar Park / Round Rock Local Pack boundaries.",
      "Treating this ring as 'Austin' only is the single most common local SEO mistake in the metro.",
      "Per-city service-area pages (Austin + Cedar Park + Round Rock) for operators in this ring typically add 2–3x qualified inbound inside 12 months.",
      "Entity ambiguity here is high — clean NAP, correct primary category, and matching GBP service list are non-negotiable.",
    ],
    searchLandscape: [
      "Northwest Austin is the geographic collision zone of three distinct Austin-metro Local Packs: Austin proper, Cedar Park, and Round Rock. Operators with a physical presence anywhere in 78727, 78729, or 78750 will appear in all three Packs — but only if their entity signals are unambiguous. Where they aren't, Google ranks the business weakly for all three metros, and a metro-first competitor eats the Pack in each.",
      "The mistake we see repeatedly: a Northwest Austin operator sets up one 'Austin' service-area page and expects to also rank for Cedar Park and Round Rock via GBP proximity alone. It rarely works. The fix is per-city service-area pages — one for Austin, one for Cedar Park, one for Round Rock — each with city-specific proof, imagery, and reviews.",
      "78750 (Jester, Anderson Mill, Balcones) tilts more strongly toward Austin identity; 78727 (Milwood, Balcones Woods) sits closer to the Cedar Park pack; 78729 (Jollyville, Anderson Mill West) is genuinely split. NAP, primary category, and GBP service list must match the primary market the operator wants to win.",
      "For B2B and higher-ticket services in this ring, AI Overview citations on 'best [service] near Cedar Park' and 'best [service] Northwest Austin' are usually winnable — the citation sets are small and often dominated by directories, leaving the operator slot open.",
    ],
    industryFocus: [
      {
        industry: "Home services & trades",
        slug: "hvac-home-services",
        note:
          "The classic multi-metro play. Per-city service-area pages for Austin, Cedar Park, and Round Rock — plus GBP service-area radii tuned per-market — routinely adds 2–3x qualified inbound.",
      },
      {
        industry: "Dental, medical & family services",
        slug: "healthcare-wellness",
        note:
          "Families in 78750 and 78727 often search 'near Cedar Park' or 'near Round Rock' even for Austin-listed practices. Cross-metro service-area pages with named-neighbourhood proof win here.",
      },
      {
        industry: "Automotive & specialist repair",
        slug: "automotive",
        note:
          "Owners in this ring cross-shop Austin, Cedar Park, and Round Rock shops. Per-city service-area pages plus review generation targeted per-metro is the pattern.",
      },
      {
        industry: "Professional services (legal, financial)",
        slug: "professional-services",
        note:
          "Buyers here search by city as often as by practice area. Author-attributed content plus city-specific case studies plus schema-rich attorney bios wins.",
      },
    ],
    topServices: [
      "local-seo",
      "google-business-profile",
      "content-strategy",
      "technical-seo",
      "reputation-management",
      "analytics-reporting",
    ],
    topZips: [
      { zip: "78727", note: "Milwood, Balcones Woods — closer to Cedar Park pack gravity; treat Cedar Park as primary." },
      { zip: "78729", note: "Jollyville, Anderson Mill West — genuinely split; requires deliberate per-metro strategy." },
      { zip: "78750", note: "Jester, Anderson Mill — tilts Austin, but Round Rock traffic is real; layer a Round Rock service-area page on top." },
    ],
    playbook: [
      "Ship a per-city service-area page for Austin, Cedar Park, and Round Rock (and Leander for northern operators) — never one blended page.",
      "Tune GBP service-area radius per-market — the correct radius for Cedar Park is not the correct radius for Austin proper.",
      "Ensure NAP consistency across every directory — this ring is where entity ambiguity costs the most.",
      "Ask for reviews that name the specific city (Austin / Cedar Park / Round Rock) the customer identifies with — this reinforces the entity signal.",
      "Track Local Pack position on all three metros monthly — if you're not tracking Cedar Park and Round Rock, you're leaving inbound on the table.",
    ],
    faqs: [
      {
        q: "Should a Northwest Austin business list themselves as Austin, Cedar Park, or Round Rock on GBP?",
        a: "Whichever primary market they most want to win. NAP, primary category, and GBP city field should all match that primary market. Additional markets are captured through service-area pages on the website, service-area radius on GBP, and city-specific review generation.",
      },
      {
        q: "Can one GBP rank in Austin, Cedar Park, AND Round Rock Local Packs?",
        a: "Yes — for many categories, especially service-area businesses. But only if the entity signals are unambiguous. Consistent NAP, one physical address, primary category matched to the strongest market, plus per-city service-area pages on the website. Get any of those wrong and you'll rank weakly in all three.",
      },
      {
        q: "What's the biggest mistake operators make in the 78727/78729/78750 ring?",
        a: "One 'Austin' service-area page, one GBP tuned only to Austin, and no city-specific proof for Cedar Park or Round Rock. Google reads the ambiguity, ranks the business weakly for both secondary metros, and metro-first competitors capture the Pack. The fix — per-city service-area pages plus targeted reviews — usually shows up in inbound calls inside 90 days.",
      },
      {
        q: "Does Optimize Plus cover Cedar Park and Round Rock too?",
        a: "Yes — see /locations/cedar-park/ and /locations/round-rock/. Many Northwest Austin engagements are actually multi-metro engagements, and we ship all three service-area pages plus a coordinated review-generation cadence.",
      },
    ],
  },
  {
    slug: "downtown-austin",
    name: "Downtown Austin / Rainey / Central",
    displaySubtitle: "78701 · 78702",
    zips: ["78701", "78702"],
    areaServed: [
      "Downtown",
      "Rainey Street",
      "Warehouse District",
      "Second Street",
      "Sixth Street",
      "Old West Austin",
    ],
    latLng: { lat: 30.2672, lng: -97.7431 },
    hero: {
      eyebrow: "Austin neighbourhood · Hospitality & aggregator SEO",
      headline: "SEO for Downtown Austin, Rainey Street, and Central Austin businesses in 78701 and 78702.",
      deck:
        "Transient-intent heavy and aggregator-dense. Direct rank against Yelp and TripAdvisor is rarely the fight — GBP quality, brand-name search capture, and reservation-platform hygiene are.",
    },
    tldr: [
      "78701 (Downtown) is the most aggregator-dominated Local Pack in Austin — Yelp, TripAdvisor, and OpenTable frequently outrank operators for category queries.",
      "The winning play here is rarely to fight aggregators — it's to capture brand-name search, own GBP, and be cited by AI Overviews for 'best' queries.",
      "AI Overview citation share on 'best [category] in Austin' is a legitimate secondary channel for Downtown operators and one many are ignoring.",
      "Event, conference, and hotel-adjacent intent is a distinct search category with its own optimization pattern.",
    ],
    searchLandscape: [
      "Downtown Austin has the most aggregator-dominated Local Pack composition of any catchment in the metro. For category queries like 'best bars downtown Austin' or 'brunch near Rainey', the first four to six results in most SERPs are aggregators — Yelp, TripAdvisor, OpenTable, Resy, Timeout — rather than operators. That reshapes what a smart SEO programme actually targets.",
      "The winning pattern for Downtown operators is rarely to try to outrank aggregators for generic category queries. It's threefold: dominate brand-name search (searches that include your business name), own the GBP surface completely (photos, hours, menu, categories, updates), and earn AI Overview citations for 'best' queries where the citation set is usually 3–5 sources.",
      "Aggregator hygiene is a specific SEO discipline in this catchment. A restaurant with an outdated menu on OpenTable, wrong hours on Yelp, and a stale hero image on TripAdvisor loses share to competitors who treat those surfaces as maintained assets. It's low-status work, and the operators who do it capture material share.",
      "Event, conference, and hotel-adjacent intent (SXSW, ACL, F1, conference-week traffic) creates predictable seasonal spikes with unusual query patterns. Content that anticipates those weeks — 'best brunch during SXSW', 'Rainey Street after ACL', 'hotel bars near Convention Center' — captures a large share of transient buyer intent that operators ignore.",
    ],
    industryFocus: [
      {
        industry: "Restaurants, bars & nightlife",
        slug: "restaurants-food",
        note:
          "Aggregator hygiene + GBP photo/menu freshness + AI Overview citation capture. Rarely worth chasing #1 organic for generic category queries against Yelp — the ROI isn't there.",
      },
      {
        industry: "Hotels, boutique lodging & short-term rental",
        slug: "hospitality",
        note:
          "Brand-search capture plus schema-rich hotel pages plus OTA parity (Booking, Expedia, Hotels.com) is what protects direct-book share. AI Overview citations on 'best boutique hotels Austin' are winnable.",
      },
      {
        industry: "Events, meetings & conference services",
        slug: "professional-services",
        note:
          "Anticipatory content around SXSW, ACL, F1, and conference weeks captures transient buyer intent — the operators who publish it consistently outperform.",
      },
      {
        industry: "Legal, financial & professional services (downtown offices)",
        slug: "professional-services",
        note:
          "Weekday-office intent dominates. Attorney/advisor bio pages with author-attributed content, schema, and Avvo/Clutch consistency drive results.",
      },
    ],
    topServices: [
      "local-seo",
      "reputation-management",
      "aeo-geo",
      "content-strategy",
      "digital-pr",
      "google-business-profile",
    ],
    topZips: [
      { zip: "78701", note: "Downtown, Rainey, Second Street, Warehouse District, Sixth Street — aggregator-dense and event-driven." },
      { zip: "78702", note: "Central-east Austin — F&B and hospitality overlap with the East Austin market; blended playbook works." },
    ],
    playbook: [
      "Treat aggregator surfaces (Yelp, TripAdvisor, OpenTable, Resy) as maintained SEO assets, not one-time setups.",
      "Capture brand-name search with a schema-rich home page, an About page, and consistent NAP across every directory.",
      "Publish anticipatory content ahead of SXSW, ACL, F1, and major conference weeks — these are the highest-leverage transient content windows.",
      "Target AI Overview citation capture on 'best [category] in Austin' and 'best [category] downtown Austin' queries — the citation set is small and often available.",
      "For hotels and lodging: audit OTA parity monthly and protect direct-book share with schema, brand search capture, and loyalty offers.",
    ],
    faqs: [
      {
        q: "Can a downtown Austin restaurant realistically rank #1 for 'best restaurants downtown Austin'?",
        a: "Rarely — and it's usually not worth trying. For that query and its variants, Yelp, TripAdvisor, Timeout, and OpenTable dominate the SERP. The higher-leverage plays are brand-name search capture, GBP dominance, aggregator hygiene, and earning the operator slot in the AI Overview citation set. Those are all winnable and compound.",
      },
      {
        q: "How much does SXSW / ACL / F1 traffic actually matter for SEO?",
        a: "For F&B, hospitality, and any hospitality-adjacent service, those weeks represent 15–35% of annual transient-intent search volume compressed into a handful of days. Operators who publish anticipatory content (guides, curated lists, event-week menus) 4–6 weeks ahead capture material share of that traffic. Operators who don't leave it to aggregators.",
      },
      {
        q: "Do reviews on Yelp still matter for downtown Austin businesses?",
        a: "Yes — but less as a rank signal for Google and more as a conversion signal for transient buyers. Downtown Austin buyers are disproportionately visitors, and visitors cross-check Yelp, TripAdvisor, and Google before deciding. A well-maintained Yelp presence with recent photos, correct hours, and thoughtful owner responses meaningfully increases conversion from those platforms.",
      },
      {
        q: "What's the fastest win for a new downtown operator?",
        a: "In our engagements: (1) GBP dominance — photos, hours, categories, menu, weekly updates. (2) Aggregator hygiene — Yelp, OpenTable, Resy, TripAdvisor all current. (3) A schema-rich brand page that owns the brand-name SERP. Those three, done well inside 60 days, usually shift both discovery and conversion measurably.",
      },
    ],
  },
];

export function getAustinNeighborhood(slug: string): AustinNeighborhood | undefined {
  return AUSTIN_NEIGHBORHOODS.find((n) => n.slug === slug);
}

export function listAustinNeighborhoods(): AustinNeighborhood[] {
  return AUSTIN_NEIGHBORHOODS;
}
